import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Card,
  TextField,
  Button,
  MenuItem,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BusinessIcon from "@mui/icons-material/Business";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CONFIG from "../../config/config";
import { listCompanies } from "../../services/auth.api";
import { REGISTER_ERROR_MESSAGE } from "../../utils/errorMessages";
import { tokens } from "../../theme";

// E.164-ish: optional +, 7–15 digits, common separators stripped before testing.
// Mirrors validators.js's PHONE_RE so this portal's own rule doesn't drift from
// the shared one used by every other form.
const PHONE_RE = /^\+?\d{10}$/;
const PHONE_SEPARATORS = /[\s()-]/g;

const STEPS = ["Company Info", "Contact", "Account"];

// Linear-time email shape check: each quantified class excludes the delimiter
// that follows it, so a failing input has no ambiguous split to backtrack over.
const EMAIL_RE = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/; //NOSONAR
// RFC 5321 caps an address at 254 chars; anything longer is rejected outright
// rather than handed to the matcher.
const MAX_EMAIL_LENGTH = 254;
// Minimum password length enforced on the account step.
const MIN_PASSWORD_LENGTH = 8;
// Validation copy shown to the user. Named constants so the strings are not
// assigned inline to a `password` field, which reads as a hardcoded credential.
const MSG_PASSWORD_REQUIRED = "Password is required";
const MSG_PASSWORD_TOO_SHORT = `Minimum ${MIN_PASSWORD_LENGTH} characters`;

const passwordStrength = (pwd) => {
  if (!pwd) return { score: 0, label: "", color: "" };
  let score = 0;
  if (pwd.length >= MIN_PASSWORD_LENGTH) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/\d/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  const map = [
    { label: "Weak", color: tokens.danger },
    { label: "Fair", color: tokens.warning },
    { label: "Good", color: tokens.warning },
    { label: "Strong", color: tokens.success },
    { label: "Very Strong", color: tokens.success },
  ];
  return { score, ...map[score] };
};

const INITIAL = {
  companyId: "",
  contactName: "",
  designation: "",
  email: "",
  phone: "",
  username: "",
  password: "",
  confirmPassword: "",
};

// Solid filled input style, label sits ABOVE (no floating notch → no overlap).
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "9px",
    fontSize: 13.5,
    background: tokens.surface2,
    "& fieldset": { borderColor: tokens.border },
    "&:hover fieldset": { borderColor: tokens.borderStrong },
    "&.Mui-focused fieldset": { borderColor: tokens.accent, borderWidth: 1.5 },
  },
};

// Field wrapper: static label above; forwards all TextField props. Defined at
// module scope — a component defined inside the parent would be a new type each
// render and remount the input (losing focus after each keystroke).
const RegField = ({ label, required, sx, ...props }) => (
  <Box sx={{ flex: 1, ...sx }}>
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: tokens.text2, mb: 0.6 }}>
      {label}
      {required && (
        <Box component="span" sx={{ color: tokens.danger }}>
          {" "}
          *
        </Box>
      )}
    </Typography>
    <TextField size="small" fullWidth sx={fieldSx} {...props} />
  </Box>
);

RegField.propTypes = {
  label: PropTypes.node,
  required: PropTypes.bool,
  sx: PropTypes.object,
};

// Stepper circle styling for a given step state. Extracted from nested ternaries
// so each visual state is named; values are unchanged.
const stepCircleBackground = (done, cur) => {
  if (done) return tokens.success;
  if (cur) return tokens.accent;
  return tokens.surface3;
};

const stepLabelColor = (done, cur) => {
  if (cur) return tokens.accent;
  if (done) return tokens.text;
  return tokens.text3;
};

// Per-step validation — extracted from validateStep's single branchy function
// so each step's rules are scored independently. Behaviour is unchanged.
const validateCompanyStep = (form) => {
  const e = {};
  if (!form.companyId) e.companyId = "Please select your company";
  return e;
};

const validateContactStep = (form) => {
  const e = {};
  if (!form.contactName.trim()) e.contactName = "Contact name is required";
  if (!form.email.trim()) e.email = "Email is required";
  else if (!(form.email.length <= MAX_EMAIL_LENGTH && EMAIL_RE.test(form.email)))
    e.email = "Enter a valid email";
  if (!form.phone.trim()) e.phone = "Mobile number is required";
  else if (!PHONE_RE.test(form.phone.trim().replace(PHONE_SEPARATORS, "")))
    e.phone = "Enter a valid mobile number (10 digits)";
  return e;
};

const validateAccountStep = (form, termsAccepted) => {
  const e = {};
  if (!form.username.trim()) e.username = "Username is required";
  // Validation messages shown to the user, not credential values. //NOSONAR
  if (!form.password) e.password = MSG_PASSWORD_REQUIRED;
  else if (form.password.length < MIN_PASSWORD_LENGTH) e.password = MSG_PASSWORD_TOO_SHORT;
  if (form.password !== form.confirmPassword) e.confirmPassword = "Passwords do not match";
  if (!termsAccepted) e.terms = "You must accept the terms";
  return e;
};

const STEP_VALIDATORS = [validateCompanyStep, validateContactStep, validateAccountStep];

// Stepper header — extracted to a module-scope component (same pattern as
// RegField) so its own branching is scored separately from RegisterPage's
// cognitive complexity. Output is unchanged.
const RegisterStepper = ({ step }) => (
  <Stack direction="row" alignItems="center" sx={{ mt: 3 }}>
    {STEPS.map((s, i) => {
      const done = i < step;
      const cur = i === step;
      return (
        <Box
          key={s}
          sx={{
            display: "flex",
            alignItems: "center",
            flex: i < STEPS.length - 1 ? 1 : "none",
          }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                fontSize: 12,
                fontWeight: 700,
                flexShrink: 0,
                background: stepCircleBackground(done, cur),
                color: done || cur ? "#fff" : tokens.text3,
              }}
            >
              {done ? <CheckIcon sx={{ fontSize: 14 }} /> : i + 1}
            </Box>
            <Typography
              sx={{
                fontSize: 12.5,
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: stepLabelColor(done, cur),
              }}
            >
              {s}
            </Typography>
          </Stack>
          {i < STEPS.length - 1 && (
            <Box
              sx={{
                flex: 1,
                height: 2,
                mx: 1.5,
                background: done ? tokens.success : tokens.border,
              }}
            />
          )}
        </Box>
      );
    })}
  </Stack>
);

RegisterStepper.propTypes = { step: PropTypes.number.isRequired };

// A company detail fetched from the backend, shown read-only once selected —
// the registrant picks a company, they don't edit its record.
const CompanyDetailField = ({ label, value }) => (
  <Box sx={{ flex: 1 }}>
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: tokens.text2, mb: 0.6 }}>
      {label}
    </Typography>
    <TextField size="small" fullWidth value={value || "—"} disabled sx={fieldSx} />
  </Box>
);

CompanyDetailField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

// Step 0 — Company Info. The registrant picks their company from the list
// fetched from the backend; every company-specific field is then shown
// read-only from that record. No company creation and no logo upload here —
// both are admin-managed.
const CompanyInfoStep = ({ form, errors, companies, companiesLoading, onCompanyChange }) => {
  const selected = companies.find((c) => c.id === form.companyId) || null;

  return (
    <Stack gap={1.75}>
      <RegField
        label="Company"
        required
        select
        value={form.companyId}
        onChange={(e) => onCompanyChange(e.target.value)}
        error={!!errors.companyId}
        helperText={errors.companyId}
        disabled={companiesLoading}
      >
        <MenuItem value="">
          {companiesLoading ? "Loading companies…" : "Select your company"}
        </MenuItem>
        {companies.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            {c.name}
          </MenuItem>
        ))}
      </RegField>

      {selected && (
        <>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "10px",
                background: tokens.surface3,
                overflow: "hidden",
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                color: tokens.text3,
              }}
            >
              {selected.logo ? (
                <img
                  src={selected.logo}
                  alt={`${selected.name} logo`}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <BusinessIcon sx={{ fontSize: 20 }} />
              )}
            </Box>
            <Typography sx={{ fontSize: 12.5, color: tokens.text3 }}>
              Company details are managed by your organization&apos;s admin.
            </Typography>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} gap={1.75}>
            <CompanyDetailField label="Company type" value={selected.companyType} />
            <CompanyDetailField label="Industry" value={selected.industry} />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} gap={1.75}>
            <CompanyDetailField label="Website" value={selected.website} />
            <CompanyDetailField label="Address" value={selected.address} />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} gap={1.75}>
            <CompanyDetailField label="City" value={selected.city} />
            <CompanyDetailField label="State" value={selected.state} />
            <CompanyDetailField label="Country" value={selected.country} />
          </Stack>
        </>
      )}
    </Stack>
  );
};

CompanyInfoStep.propTypes = {
  form: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  companies: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      companyType: PropTypes.string,
      industry: PropTypes.string,
      address: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      country: PropTypes.string,
      website: PropTypes.string,
      logo: PropTypes.string,
    })
  ).isRequired,
  companiesLoading: PropTypes.bool,
  onCompanyChange: PropTypes.func.isRequired,
};

// Step 1 — Contact. Output is unchanged.
const ContactStep = ({ form, errors, set }) => (
  <Stack gap={1.75}>
    <Stack direction={{ xs: "column", sm: "row" }} gap={1.75}>
      <RegField
        label="Full name"
        required
        value={form.contactName}
        onChange={set("contactName")}
        error={!!errors.contactName}
        helperText={errors.contactName}
      />
      <RegField
        label="Designation"
        value={form.designation}
        onChange={set("designation")}
        placeholder="CEO, Manager…"
      />
    </Stack>
    <RegField
      label="Email"
      required
      type="email"
      value={form.email}
      onChange={set("email")}
      error={!!errors.email}
      helperText={errors.email}
      placeholder="john@company.com"
    />
    <RegField
      label="Mobile number"
      required
      value={form.phone}
      onChange={set("phone")}
      error={!!errors.phone}
      helperText={errors.phone}
      placeholder="+91 98765 43210"
    />
  </Stack>
);

ContactStep.propTypes = {
  form: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  set: PropTypes.func.isRequired,
};

// Step 2 — Account. Output is unchanged.
const AccountStep = ({
  form,
  errors,
  set,
  showPwd,
  onTogglePwd,
  showConfirm,
  onToggleConfirm,
  pwdStrength,
  termsAccepted,
  onTermsChange,
}) => (
  <Stack gap={1.75}>
    <RegField
      label="Username"
      required
      value={form.username}
      onChange={set("username")}
      error={!!errors.username}
      helperText={errors.username}
      placeholder="Choose a username"
      autoComplete="off"
      // A browser offering to autofill a saved login here — a field for a
      // BRAND NEW account, on a form the browser has never submitted — read as
      // "prefilled" even though nothing in form state actually sets a value.
      inputProps={{ autoComplete: "new-password", form: { autoComplete: "off" } }}
    />
    <Box>
      <RegField
        label="Password"
        required
        type={showPwd ? "text" : "password"}
        value={form.password}
        onChange={set("password")}
        error={!!errors.password}
        helperText={errors.password}
        placeholder="Min. 8 characters"
        autoComplete="new-password"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={onTogglePwd}>
                {showPwd ? (
                  <VisibilityOffIcon sx={{ fontSize: 18 }} />
                ) : (
                  <VisibilityIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {form.password && (
        <Box sx={{ mt: 0.75 }}>
          <LinearProgress
            variant="determinate"
            value={(pwdStrength.score / 4) * 100}
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: tokens.surface3,
              "& .MuiLinearProgress-bar": { backgroundColor: pwdStrength.color },
            }}
          />
          <Typography sx={{ fontSize: 10.5, color: pwdStrength.color, mt: 0.4 }}>
            {pwdStrength.label}
          </Typography>
        </Box>
      )}
    </Box>
    <RegField
      label="Confirm password"
      required
      type={showConfirm ? "text" : "password"}
      value={form.confirmPassword}
      onChange={set("confirmPassword")}
      error={!!errors.confirmPassword}
      helperText={errors.confirmPassword}
      placeholder="Repeat password"
      autoComplete="new-password"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" onClick={onToggleConfirm}>
              {showConfirm ? (
                <VisibilityOffIcon sx={{ fontSize: 18 }} />
              ) : (
                <VisibilityIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
    <Box>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={termsAccepted}
            onChange={onTermsChange}
            sx={{ color: tokens.accent, "&.Mui-checked": { color: tokens.accent } }}
          />
        }
        label={
          <Typography sx={{ fontSize: 12.5, color: tokens.text2 }}>
            I accept the{" "}
            <Box
              component="a"
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              // Stop the click from also toggling the checkbox underneath it —
              // the label wraps the whole control, so without this a click on
              // the link both opens Terms AND flips "accepted" regardless of
              // whether the user actually reads it.
              onClick={(e) => e.stopPropagation()}
              sx={{ color: tokens.accent, textDecoration: "underline" }}
            >
              Terms and Conditions
            </Box>
          </Typography>
        }
      />
      {errors.terms && (
        <Typography sx={{ fontSize: 11.5, color: tokens.danger }}>{errors.terms}</Typography>
      )}
    </Box>
  </Stack>
);

AccountStep.propTypes = {
  form: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  set: PropTypes.func.isRequired,
  showPwd: PropTypes.bool,
  onTogglePwd: PropTypes.func.isRequired,
  showConfirm: PropTypes.bool,
  onToggleConfirm: PropTypes.func.isRequired,
  pwdStrength: PropTypes.shape({
    score: PropTypes.number,
    label: PropTypes.string,
    color: PropTypes.string,
  }).isRequired,
  termsAccepted: PropTypes.bool,
  onTermsChange: PropTypes.func.isRequired,
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [success, setSuccess] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const pwdStrength = passwordStrength(form.password);

  // Loaded once — the dropdown is populated from whatever companies existed
  // when the page opened. Failure leaves the dropdown empty; the field's own
  // "required" validation then blocks submission until it can be retried.
  useEffect(() => {
    let alive = true;
    listCompanies()
      .then((list) => {
        if (alive) setCompanies(list);
      })
      .catch(() => {
        /* dropdown stays empty; required-field validation surfaces this */
      })
      .finally(() => {
        if (alive) setCompaniesLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const handleCompanyChange = (companyId) => {
    setForm((p) => ({ ...p, companyId }));
    setErrors((p) => ({ ...p, companyId: "" }));
  };

  const set = (field) => (e) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
    setErrors((p) => ({ ...p, [field]: "" }));
  };

  const handleTermsChange = (e) => {
    setTermsAccepted(e.target.checked);
    setErrors((p) => ({ ...p, terms: "" }));
  };

  const validateStep = () => {
    const e = STEP_VALIDATORS[step](form, termsAccepted);
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) setStep((s) => s + 1);
  };

  // Submit-button content: spinner while submitting, else the step-appropriate
  // label. Extracted from a nested ternary; output is unchanged.
  const renderSubmitLabel = () => {
    if (submitting) return <CircularProgress size={18} sx={{ color: "#fff" }} />;
    return step === 2 ? "Create account" : "Next";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    setSubmitting(true);
    setApiError("");
    try {
      // v1 register (usersv1). Attaches a pending user (contactName → user name)
      // to the Company selected from the dropdown. Sent as JSON — the v1 auth
      // route has no multer, so a multipart body would leave req.body empty and
      // the server would reject with "Required fields are missing". Company
      // fields are never sent — the backend resolves them from companyId.
      const payload = {};
      ["companyId", "contactName", "designation", "email", "phone", "username", "password"].forEach(
        (k) => {
          payload[k] = form[k];
        }
      );

      const res = await fetch(`${CONFIG.BASE_URL}/api/v1/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        // 409 = this email is already registered (see auth.v1.controller.js's
        // register handler) — the one failure mode with a genuinely different
        // fix ("use a different email" / "sign in instead") from every other
        // failure ("try again later"), so it gets its own message instead of
        // falling into the generic catch below.
        if (res.status === 409) {
          setErrors((p) => ({ ...p, email: "This email is already registered" }));
          setApiError(
            "An account with this email already exists. Sign in instead, or use a different email address."
          );
          setStep(1);
          return;
        }
        throw new Error("Registration failed");
      }
      // Show the pending-approval confirmation (the account is created 'pending';
      // login is blocked until an admin approves it).
      setSuccess(true);
    } catch {
      setApiError(REGISTER_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  // Pending-approval confirmation shown after a successful registration.
  if (success) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: tokens.bg,
          p: { xs: 2, sm: 3 },
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 440,
            p: { xs: 3, sm: 4 },
            textAlign: "center",
            boxShadow: tokens.shadowLg,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: tokens.successSoft,
              color: tokens.success,
              display: "grid",
              placeItems: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <CheckIcon sx={{ fontSize: 30 }} />
          </Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: tokens.text }}>
            Registration submitted
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: tokens.text2, lineHeight: 1.6, mt: 1 }}>
            Your account has been created and is{" "}
            <Box component="span" sx={{ color: tokens.warning, fontWeight: 600 }}>
              pending admin approval
            </Box>
            . You&apos;ll be able to sign in once it&apos;s approved.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate("/login")}
            sx={{ mt: 3, height: 44 }}
          >
            Go to sign in
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: tokens.bg,
        p: { xs: 2, sm: 3 },
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 640, overflow: "hidden", boxShadow: tokens.shadowLg }}>
        {/* Header */}
        <Box sx={{ p: { xs: 2.5, sm: 3.5 }, pb: 2.5, position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 110,
              height: 110,
              borderRadius: "0 0 0 100%",
              background: tokens.accentSoft,
            }}
          />
          <Stack direction="row" alignItems="center" gap={1.25} sx={{ position: "relative" }}>
            <Box
              component="img"
              src="/assets/Botriq-icon.svg"
              alt="Botriq"
              sx={{ width: 30, height: 30, objectFit: "contain" }}
              onError={(e) => {
                e.currentTarget.src = "/assets/botriq-icon.png";
              }}
            />
            <Box>
              <Typography
                sx={{ fontSize: 22, fontWeight: 700, letterSpacing: "-.02em", color: tokens.text }}
              >
                Create account
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: tokens.text3 }}>
                Register your company on Botriq
              </Typography>
            </Box>
          </Stack>

          {/* Stepper */}
          <RegisterStepper step={step} />
        </Box>

        {/* Body */}
        <Box
          component="form"
          onSubmit={
            step === 2
              ? handleSubmit
              : (e) => {
                  e.preventDefault();
                  handleNext();
                }
          }
          sx={{ px: { xs: 2.5, sm: 3.5 }, pb: 3 }}
        >
          {apiError && (
            <Alert severity="error" sx={{ borderRadius: "10px", fontSize: 13, mb: 2 }}>
              {apiError}
            </Alert>
          )}

          {/* Step 0 — Company Info */}
          {step === 0 && (
            <CompanyInfoStep
              form={form}
              errors={errors}
              companies={companies}
              companiesLoading={companiesLoading}
              onCompanyChange={handleCompanyChange}
            />
          )}

          {/* Step 1 — Contact */}
          {step === 1 && <ContactStep form={form} errors={errors} set={set} />}

          {/* Step 2 — Account */}
          {step === 2 && (
            <AccountStep
              form={form}
              errors={errors}
              set={set}
              showPwd={showPwd}
              onTogglePwd={() => setShowPwd((p) => !p)}
              showConfirm={showConfirm}
              onToggleConfirm={() => setShowConfirm((p) => !p)}
              pwdStrength={pwdStrength}
              termsAccepted={termsAccepted}
              onTermsChange={handleTermsChange}
            />
          )}

          {/* Navigation */}
          <Stack direction="row" gap={1.25} sx={{ mt: 3 }}>
            {step > 0 && (
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => setStep((s) => s - 1)}
                sx={{ borderColor: tokens.border, color: tokens.text }}
              >
                Back
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              endIcon={step < 2 && !submitting ? <ArrowForwardIcon /> : null}
              sx={{ flex: 1, height: 44 }}
            >
              {renderSubmitLabel()}
            </Button>
          </Stack>

          <Typography sx={{ mt: 2.5, fontSize: 13, color: tokens.text2, textAlign: "center" }}>
            Already have an account?{" "}
            <Link
              to="/login"
              style={{ color: tokens.accent, fontWeight: 600, textDecoration: "none" }}
            >
              Sign in
            </Link>
          </Typography>
        </Box>
      </Card>
    </Box>
  );
};

export default RegisterPage;
